import { useEffect, useMemo, useState } from "react";
import { endOfDay, startOfDay } from "date-fns";
import PageTitle from "@/components/PageTitle";
import DashLayout from "@/layouts/DashLayout";
import { useAppSelector } from "@/redux/features/hooks";
import ReactPagination from "@/components/Table/Pagination";
import { usePagination } from "@/lib/hooks";
import OrdersList from "@/components/order/OrdersList";
import OrdersTable from "@/components/order/OrdersTable";
import { useGetOrdersQuery } from "@/redux/services/order/order.api";
import { LayoutGrid, List } from "lucide-react";
import { cn } from "@/lib/utils";

type ViewMode = "cards" | "list";
const VIEW_MODE_KEY = "orders.viewMode";

// The API status filter is include-only (no exclude operator), so the day's
// "real" order count is queried by listing every non-cancelled status, and the
// cancelled/rejected count is queried separately. Keeping them distinct means
// cancelled orders never inflate the headline total (SFRESTO-87).
const REAL_ORDER_STATUSES =
  "pending,accepted,preparing,ready,served,picked_up,delivered";
const CANCELLED_ORDER_STATUSES = "canceled,rejected";

const Orders = () => {
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  const { limit, page, onChange } = usePagination(30);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [viewMode, setViewMode] = useState<ViewMode>(
    () =>
      (localStorage.getItem(VIEW_MODE_KEY) as ViewMode | null) ?? "cards"
  );

  useEffect(() => {
    localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  const todayRange = useMemo(() => {
    const now = new Date();
    return {
      "createdAt[after]": startOfDay(now).toISOString(),
      "createdAt[before]": endOfDay(now).toISOString(),
    };
  }, []);

  const { data: realCountData } = useGetOrdersQuery(
    {
      restaurantId: restaurant?.id ?? "",
      query: { limit: 1, status: REAL_ORDER_STATUSES, ...todayRange },
    },
    { skip: !restaurant?.id || viewMode !== "cards" }
  );
  const { data: cancelledCountData } = useGetOrdersQuery(
    {
      restaurantId: restaurant?.id ?? "",
      query: { limit: 1, status: CANCELLED_ORDER_STATUSES, ...todayRange },
    },
    { skip: !restaurant?.id || viewMode !== "cards" }
  );
  const realCount = realCountData?.count;
  const cancelledCount = cancelledCountData?.count;

  if (!restaurant) {
    return <div>Loading restaurant data...</div>;
  }

  const cardsDescription =
    realCount !== undefined
      ? cancelledCount
        ? `Commandes du jour · ${realCount} · ${cancelledCount} annulée${
            cancelledCount > 1 ? "s" : ""
          }`
        : `Commandes du jour · ${realCount}`
      : "Commandes du jour";

  return (
    <DashLayout>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <PageTitle
            title="Commandes"
            description={
              viewMode === "cards"
                ? cardsDescription
                : "Historique complet des commandes"
            }
          />
          <div className="inline-flex items-center rounded-lg border border-border bg-white p-0.5 shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode("cards")}
              className={cn(
                "flex items-center justify-center p-1.5 rounded-md transition-colors",
                viewMode === "cards"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-card-foreground"
              )}
              aria-label="Affichage en cartes"
              aria-pressed={viewMode === "cards"}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={cn(
                "flex items-center justify-center p-1.5 rounded-md transition-colors",
                viewMode === "list"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-card-foreground"
              )}
              aria-label="Affichage en liste"
              aria-pressed={viewMode === "list"}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>

        {viewMode === "cards" ? (
          <>
            <OrdersList
              restaurantId={restaurant.id}
              limit={limit}
              page={page}
              dateScope="today"
              onTotalCountChange={setTotalCount}
            />
            {totalCount > limit && (
              <ReactPagination
                itemsPerPage={limit}
                count={totalCount}
                onChange={onChange}
              />
            )}
          </>
        ) : (
          <OrdersTable restaurantId={restaurant.id} />
        )}
      </div>
    </DashLayout>
  );
};

export default Orders;
